/**
 * Side-pot construction from per-player total contributions.
 *
 * Every contribution level creates a pot that is contested only by the non-folded players who
 * contributed at least that level. Folded players' chips stay in the pots they reached but they are
 * never eligible. A pot with a single eligible player is an uncalled bet and is simply returned.
 */
export interface Contribution {
  seat: number;
  amount: number;
  folded: boolean;
}

export interface Pot {
  amount: number;
  eligible: number[];
}

export function buildPots(contribs: readonly Contribution[]): Pot[] {
  const levels = [...new Set(contribs.filter((c) => !c.folded && c.amount > 0).map((c) => c.amount))].sort((a, b) => a - b);
  const pots: Pot[] = [];
  let prev = 0;
  for (const level of levels) {
    let amount = 0;
    for (const c of contribs) amount += Math.max(0, Math.min(c.amount, level) - prev);
    const eligible = contribs.filter((c) => !c.folded && c.amount >= level).map((c) => c.seat);
    if (amount > 0) pots.push({ amount, eligible });
    prev = level;
  }
  // Folded chips above the highest live level (e.g. a bettor folding to a re-raise all-in for less is impossible,
  // but a folded big bet above all live players' contributions can happen) go to the last pot.
  let leftover = 0;
  for (const c of contribs) leftover += Math.max(0, c.amount - prev);
  if (leftover > 0) {
    if (pots.length) pots[pots.length - 1]!.amount += leftover;
    else pots.push({ amount: leftover, eligible: contribs.filter((c) => !c.folded).map((c) => c.seat) });
  }
  // Merge adjacent pots with identical eligibility (cosmetic, keeps UI simple).
  const merged: Pot[] = [];
  for (const p of pots) {
    const last = merged[merged.length - 1];
    if (last && last.eligible.length === p.eligible.length && last.eligible.every((s, i) => s === p.eligible[i])) last.amount += p.amount;
    else merged.push({ amount: p.amount, eligible: [...p.eligible] });
  }
  return merged;
}
